"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
    ProductCard as UICard,
    type ProductCardModel as UICardModel,
    type ProductCardVariantKey,
} from "@dionatanbig2be/big2be-ui";
// Puras e compartilhadas com o servidor — ver o cabeçalho de ./variant.
import { cardOrientation, normalizeVariant, type CardOrientation } from "./variant";
import { flyToCart, useCartStore } from "@/modules/cart";
import { resolveProductImageUrl } from "@/shared/helpers/media";
import { useAddToCartBehavior, useOutOfStockDisplay } from "@/shell/hooks/useBootstrap";
import { useTheme } from "@/theme";
import { useFavoriteProduct } from "./useFavoriteProduct";
import { useStockAlert } from "../product/useStockAlert";
import { snapQuantity } from "./quantity";
import type { ProductCardModel } from "./model";

/**
 * O stepper do DS dispara `onQuantityChange` a cada tecla quando a pessoa
 * digita a quantidade. Sem esperar, cada dígito vira um PUT no carrinho (e o
 * store descarta os concorrentes). Este intervalo junta a digitação num envio.
 */
const QUANTITY_DEBOUNCE_MS = 300;

export type { ProductCardVariantKey };

// Reexportadas para não quebrar quem já importava daqui. A definição mora em
// ./variant porque Server Components também as chamam.
export { cardOrientation, normalizeVariant };
export type { CardOrientation };

interface Props {
    product: ProductCardModel;
    variant: ProductCardVariantKey;
    /** Primeiro card acima da dobra → imagem com priority (LCP). */
    priority?: boolean;
    /** `"eager"` baixa a foto sem esperar o card chegar perto da tela. Omitido = lazy. */
    imageLoading?: "lazy" | "eager";
    /** Card deitado ocupa a largura do container. */
    block?: boolean;
    /**
     * Ignora a configuração da loja e adiciona direto, sem virar stepper.
     *
     * Para UI TRANSITÓRIA — dropdown e modal de busca, card do chat — que fecha
     * logo depois de adicionar: transformar num stepper uma superfície que some
     * em seguida deixa o cliente sem onde ajustar o que acabou de escolher.
     */
    disableQuantityStepper?: boolean;
    /** `false` esconde o preço (com riscado e selo). Default `true`. */
    showPrice?: boolean;
    /** `false` tira o botão de adicionar ao carrinho. Default `true`. */
    cart?: boolean;
    /** Avisa o pai quando o favorito muda (o painel de favoritos remove o card). */
    onFavoriteChange?: (favorited: boolean) => void;
    className?: string;
}

/**
 * Card de produto — ilha client sobre o card do design system.
 *
 * Duas coisas explicam o desenho:
 *
 * O produto que chega é PURO CATÁLOGO, igual para todo visitante. O que é do
 * usuário — o coração — é resolvido aqui dentro, pelo store, depois da
 * hidratação. Por isso o HTML do servidor pode ir para a borda.
 *
 * Não existe assinatura de estado GLOBAL de carrinho. `busy` é local: um card
 * só precisa se desabilitar enquanto a própria mutação está em voo. Assinar o
 * `pending` da store faria todo card re-renderizar a cada clique em qualquer
 * outro — com 96 cards na tela, é o que trava o INP.
 */
/**
 * Qual produto o "Adicionar" leva ao carrinho.
 *
 * Cada opção de variação é um PRODUTO próprio — o `id` da opção É o
 * `productId`. Sem resolver isto, o card adiciona sempre o produto de origem: o
 * cliente escolhe "20 kg" e leva "10 kg".
 *
 * Com mais de um grupo (Peso E Sabor) o backend não modela a COMBINAÇÃO — cada
 * grupo lista produtos inteiros da mesma família. Vence a primeira escolha que
 * aponte para um produto diferente do de origem, que é a que o visitante
 * acabou de mudar; sem nenhuma, fica o de origem.
 */
function selectedVariation(
    product: ProductCardModel,
    selectedByGroup: Record<number, string>
): { productId: number; image: string | null; inStock: boolean } {
    for (const group of product.variations ?? []) {
        const chosen = group.options.find((option) => option.id === selectedByGroup[group.aggregationId]);
        if (chosen && Number(chosen.id) !== product.productId) {
            return {
                productId: Number(chosen.id),
                image: chosen.image ?? product.image,
                inStock: chosen.inStock ?? product.inStock,
            };
        }
    }
    return { productId: product.productId, image: product.image, inStock: product.inStock };
}

export function ProductCard({
    product,
    variant,
    priority,
    imageLoading,
    block,
    disableQuantityStepper,
    onFavoriteChange,
    showPrice,
    cart,
    className,
}: Props) {
    const favorite = useFavoriteProduct(product.productId, onFavoriteChange);
    const [busy, setBusy] = useState(false);
    const quantityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (quantityTimer.current) clearTimeout(quantityTimer.current);
        },
        []
    );

    // A configuração "escolher quantidade" é da LOJA e vale em toda superfície.
    // É lida AQUI, não em cada chamador: quando cada tela decidia por conta
    // própria, só a listagem passava a prop — home, favoritos, relacionados e
    // busca adicionavam direto e abriam o carrinho, contrariando a configuração.
    //
    // Hook fora de curto-circuito: `!x && useHook()` não chamaria o hook quando
    // `x` é true, e a ordem dos hooks tem de ser a mesma em todo render.
    const storeBehavior = useAddToCartBehavior();
    const quantityStepper = !disableQuantityStepper && storeBehavior === "quantity_stepper";

    // Selo de desconto (formato e desenho): definido na identidade visual, vale
    // em toda superfície pelo mesmo motivo acima. Sem a chave, os mesmos
    // defaults que o portal mostra.
    const cardTheme = useTheme()?.components?.card;
    const discountDisplay = cardTheme?.discountDisplay === "amount" ? "amount" : "percent";
    const discountStyle = cardTheme?.discountStyle === "savings" ? "savings" : "tag";
    const showBrand = cardTheme?.showBrand !== false;

    // Esgotado no modo notify_me: o sino registra o aviso em vez de adicionar.
    const notifyInsteadOfAdd = useOutOfStockDisplay() === "notify_me" && !product.inStock;
    const stockAlert = useStockAlert(product.productId);

    // QUAL produto este card colocou no carrinho. Com variação NÃO é
    // `product.productId`: cada opção é um produto próprio, e o stepper que
    // aparece depois precisa mexer no que foi de fato adicionado. O DS não
    // informa a seleção no `onQuantityChange`, então quem lembra é o card.
    const [addedProductId, setAddedProductId] = useState<number | null>(null);
    const trackedId = addedProductId ?? product.productId;

    // Booleano, não o carrinho inteiro: só muda quando ESTE produto entra ou
    // sai — nenhum outro card re-renderiza por causa disso.
    const inCart = useCartStore((state) =>
        Boolean(state.cart?.items?.some((item) => item.productId === trackedId))
    );

    // O card do DS guarda a quantidade INTERNAMENTE e não aceita valor
    // controlado. Quando o item sai do carrinho por fora (drawer, checkout,
    // outra aba), remontar é a única forma de ele voltar a mostrar
    // "Adicionar" — e só NESSA transição: remontar também na entrada desfaria
    // o stepper no instante em que ele aparece.
    //
    // O alvo observado é guardado junto: trocar de produto (escolheu outra
    // variação) REBASEIA sem disparar reset, senão o card se remontaria
    // sozinho quando o produto de origem já estivesse no carrinho.
    const [tracked, setTracked] = useState({ id: trackedId, inCart });
    const [resetToken, setResetToken] = useState(0);

    if (tracked.id !== trackedId) {
        setTracked({ id: trackedId, inCart });
    } else if (tracked.inCart !== inCart) {
        setTracked({ id: trackedId, inCart });
        if (tracked.inCart) {
            setResetToken((token) => token + 1);
            setAddedProductId(null);
        }
    }

    const uiProduct = useMemo<UICardModel>(
        () => ({
            ...product,
            // Sem marca o card do DS não desenha a linha.
            brand: showBrand ? product.brand : undefined,
            image: resolveProductImageUrl(product.image) ?? "",
            variations: product.variations?.map((group) => ({
                ...group,
                options: group.options.map((option) => ({
                    ...option,
                    image: option.image ? resolveProductImageUrl(option.image) : option.image,
                })),
            })),
        }),
        [product, showBrand]
    );

    const addToCart = async (
        selectedByGroup: Record<number, string>,
        quantity: number,
        trigger: HTMLButtonElement
    ) => {
        // O estoque que vale é o do produto ESCOLHIDO: com origem esgotada e a
        // variação disponível, o card exibe a variação e o "Adicionar" tem de ir.
        const target = selectedVariation(product, selectedByGroup);
        if (busy || !target.inStock) return;
        setBusy(true);
        try {
            // Guarda ANTES da chamada: o stepper que aparece em seguida precisa
            // saber em qual produto mexer, mesmo se a mutação demorar.
            setAddedProductId(target.productId);
            // A animação usa a foto do que foi ESCOLHIDO — voar a imagem do
            // produto de origem entrega ao cliente que fomos buscar o errado.
            flyToCart(trigger, resolveProductImageUrl(target.image));
            await useCartStore
                .getState()
                .add(
                    target.productId,
                    // Piso/passo/inteiro do produto: a nest recusa o que foge disso.
                    snapQuantity(quantity, product.saleRules) || product.saleRules.minPurchase,
                    { silent: quantityStepper }
                );
        } finally {
            setBusy(false);
        }
    };

    /**
     * Mudança no stepper: `0` remove na hora (o DS manda 0 ao descer abaixo do
     * piso). Qualquer outro valor é ajustado às regras do produto e enviado
     * depois de uma pausa na digitação — o último valor vence.
     */
    const changeQuantity = (quantity: number) => {
        if (quantityTimer.current) clearTimeout(quantityTimer.current);
        if (quantity <= 0) {
            void useCartStore.getState().updateQuantity(trackedId, 0);
            return;
        }
        const snapped = snapQuantity(quantity, product.saleRules);
        quantityTimer.current = setTimeout(() => {
            quantityTimer.current = null;
            void useCartStore.getState().updateQuantity(trackedId, snapped);
        }, QUANTITY_DEBOUNCE_MS);
    };

    // Selos e rodapé ("Entrega grátis acima de R$ 99") vão no MODELO e o card
    // do DS desenha os dois sobre a imagem, sem mexer na altura. Nada de embrulho
    // aqui: o card sai sempre "nu", com a `className` do chamador — é ele quem
    // ocupa a célula da grade (o `[&>article]` do FIT_TO_COLUMN depende disso).
    return (
        <UICard
            key={resetToken}
            variant={variant}
            product={uiProduct}
            priority={priority}
            imageLoading={imageLoading}
            block={block}
            className={className}
            quantitySelector={quantityStepper}
            showPrice={showPrice}
            cart={cart}
            discountDisplay={discountDisplay}
            discountStyle={discountStyle}
            isFavorite={favorite.isFavorited}
            onToggleFavorite={() => void favorite.toggle()}
            onAddToCart={(selection, event) =>
                void addToCart(
                    selection.selectedByGroup,
                    selection.quantity,
                    event.currentTarget as HTMLButtonElement
                )
            }
            // Sem isto o sino de "avise-me" cai no `onAddToCart` do DS e tenta
            // ADICIONAR um produto esgotado — que o backend recusa.
            onNotifyMe={notifyInsteadOfAdd ? () => void stockAlert.toggle() : undefined}
            notifyMeActive={stockAlert.active}
            onQuantityChange={changeQuantity}
            disabled={busy}
        />
    );
}
